import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';

import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import rehypeRaw from 'rehype-raw';
import TurndownService from 'turndown';

import { useCookies } from 'react-cookie';
import { useNavigate, useParams } from 'react-router-dom';
import { API_BASE_URL, ENDPOINTS } from '../../config';
import CodeBlock from './CodeBlock';
import MainHeader from '../Sidebar/MainHeader';
import { Notyf } from 'notyf';
import './Chat.css';

const notyf = new Notyf({ position: { x: 'right', y: 'top' }, ripple: true, dismissible: true });
const DEFAULT_API_URL = `${API_BASE_URL || 'https://ai.holaai.in'}/chats/generic-ai`;

const LANGS = [
  { code: 'und', label: 'Auto-detect', tag: null },
  { code: 'hi', label: 'Hindi', tag: 'hi-IN' },
  { code: 'mr', label: 'Marathi', tag: 'mr-IN' },
  { code: 'bn', label: 'Bengali', tag: 'bn-IN' },
  { code: 'gu', label: 'Gujarati', tag: 'gu-IN' },
  { code: 'pa', label: 'Punjabi', tag: 'pa-IN' },
  { code: 'ta', label: 'Tamil', tag: 'ta-IN' },
  { code: 'te', label: 'Telugu', tag: 'te-IN' },
  { code: 'kn', label: 'Kannada', tag: 'kn-IN' },
  { code: 'ml', label: 'Malayalam', tag: 'ml-IN' },
  { code: 'ur', label: 'Urdu', tag: 'ur-IN' },
  { code: 'en', label: 'English', tag: 'en-IN' }
];

const LANG_TAGS = {
  hi: 'hi-IN', mr: 'mr-IN', bn: 'bn-IN', gu: 'gu-IN', pa: 'pa-IN',
  ta: 'ta-IN', te: 'te-IN', kn: 'kn-IN', ml: 'ml-IN', ur: 'ur-IN',
  en: 'en-IN', und: null
};

/* ====================== TTS HELPERS ===================== */
// HTML → plain text
const htmlToText = (html) => {
  const el = document.createElement('div');
  el.innerHTML = html || '';
  return (el.textContent || el.innerText || '').trim();
};

// Strip emojis (fallback if Unicode properties unsupported)
const stripEmojis = (s) => {
  try { return s.replace(/\p{Extended_Pictographic}|\p{Emoji_Presentation}/gu, ' '); }
  catch { return s.replace(/([\u2190-\u21FF]|[\u2300-\u27BF]|[\uE000-\uF8FF]|[\uD83C-\uDBFF][\uDC00-\uDFFF]|\uFE0F|\u200D)/g, ' '); }
};

const collapseWS = (s) => s.replace(/\s+/g, ' ').trim();

// Prepare any raw (md/html/mixed) for TTS (fallback if DOM text isn’t available)
const prepareTTSContent = (raw) => {
  if (!raw) return '';
  let s = String(raw);

  // remove code (block + inline)
  s = s.replace(/```[\s\S]*?```/g, ' ').replace(/`[^`]+`/g, ' ');

  // strip markdown noise
  s = s
    .replace(/!\[[^\]]*\]\([^\)]*\)/g, ' ')    // images
    .replace(/\[[^\]]*\]\([^\)]*\)/g, ' ')    // links
    .replace(/(^|\n)\s*\|.*\|\s*(\n|$)/g, ' ')// table rows
    .replace(/[*_~#>+-]{1,}/g, ' ');          // decorations

  // render HTML to text
  s = htmlToText(s);

  // remove URLs & emails
  s = s.replace(/https?:\/\/\S+/g, ' ').replace(/\S+@\S+\.\S+/g, ' ');

  // strip emojis & collapse
  return collapseWS(stripEmojis(s));
};

// chunk long text for speech
const TTS_MAX_CHUNK = 220;
const chunkForTTS = (text, maxLen = TTS_MAX_CHUNK) => {
  const chunks = [];
  let remaining = text;
  while (remaining.length > maxLen) {
    let cut = remaining.lastIndexOf('.', maxLen);
    if (cut < maxLen * 0.6) cut = remaining.lastIndexOf(' ', maxLen);
    if (cut <= 0) cut = maxLen;
    chunks.push(remaining.slice(0, cut).trim());
    remaining = remaining.slice(cut).trim();
  }
  if (remaining) chunks.push(remaining);
  return chunks;
};

// wait for voices (Chrome/Edge async load)
const waitForVoices = () =>
  new Promise((resolve) => {
    if (!('speechSynthesis' in window)) return resolve([]);
    const have = window.speechSynthesis.getVoices();
    if (have && have.length) return resolve(have);
    const t = setInterval(() => {
      const v = window.speechSynthesis.getVoices();
      if (v && v.length) { clearInterval(t); resolve(v); }
    }, 100);
    setTimeout(() => { clearInterval(t); resolve(window.speechSynthesis.getVoices() || []); }, 3000);
  });

// Chrome pause bug nudge
const nudgeResume = () => {
  try {
    if (window.speechSynthesis.paused) window.speechSynthesis.resume();
    const id = setInterval(() => {
      if (window.speechSynthesis.paused) window.speechSynthesis.resume();
      else clearInterval(id);
    }, 200);
    setTimeout(() => clearInterval(id), 1200);
  } catch { }
};

// pick best voice; fallback keeps utterance.lang
const pickVoiceForLang = (voices, langCode) => {
  const tag = LANG_TAGS[langCode] || null;
  if (!voices || voices.length === 0) return { voice: null, tag };
  const lc = (s) => (s || '').toLowerCase();
  if (tag) {
    const exact = voices.find(v => lc(v.lang) === lc(tag));
    if (exact) return { voice: exact, tag };
    const base = tag.split('-')[0];
    const langOnly = voices.find(v => lc(v.lang).startsWith(base));
    if (langOnly) return { voice: langOnly, tag };
    const hintsMap = {
      hi: ['hindi', 'india', 'heera', 'kalpana', 'harman'],
      mr: ['marathi'], bn: ['bengali', 'bangla'], gu: ['gujarati'], pa: ['punjabi'],
      ta: ['tamil'], te: ['telugu'], kn: ['kannada'], ml: ['malayalam'], ur: ['urdu']
    };
    const hints = hintsMap[base] || [];
    const byName = voices.find(v => hints.some(h => lc(v.name).includes(h)));
    if (byName) return { voice: byName, tag };
  }
  const def = voices.find(v => v.default) || voices[0] || null;
  return { voice: def, tag };
};

/* ========================== COMPONENT ========================== */
const Chat = ({ sidebarOpen, setSidebarOpen }) => {
  const { chatId } = useParams();
  const navigate = useNavigate();

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [isThinking, setIsThinking] = useState(false);
  const [copiedCodeId, setCopiedCodeId] = useState(null);

  const [selectedLang, setSelectedLang] = useState('hi');
  const [detectedLang, setDetectedLang] = useState('und');
  const [inputCameFromVoice, setInputCameFromVoice] = useState(false); // auto-speak only for voice queries

  // TTS state/refs
  const [ttsBusy, setTtsBusy] = useState(false);
  const ttsVoicesRef = useRef([]);
  const ttsAbortRef = useRef(false);
  const currentUtteranceRef = useRef(null);

  const messagesEndRef = useRef(null);
  const recognition = useRef(null);
  const turndownRef = useRef(null);
  const finalTranscript = useRef('');
  const copyTimeouts = useRef({});
  const [cookies] = useCookies(['token']);

  const msgRefs = useRef({}); // index -> DOM node

  /* ---------- TTS Init ---------- */
  useEffect(() => {
    if (!('speechSynthesis' in window)) return;
    const load = () => { ttsVoicesRef.current = window.speechSynthesis.getVoices() || []; };
    load();
    window.speechSynthesis.onvoiceschanged = load;
    return () => { window.speechSynthesis.onvoiceschanged = null; };
  }, []);

  const stopSpeaking = () => {
    ttsAbortRef.current = true;
    try { window.speechSynthesis.cancel(); } catch { }
    if (currentUtteranceRef.current) {
      try {
        currentUtteranceRef.current.onend = null;
        currentUtteranceRef.current.onerror = null;
      } catch { }
      currentUtteranceRef.current = null;
    }
    setTtsBusy(false);
  };

  const speakText = async (cleanVisibleText, langCode) => {
    if (!('speechSynthesis' in window)) return;
    const clean = (cleanVisibleText || '').trim();
    if (!clean) return;

    ttsVoicesRef.current = await waitForVoices();
    const voices = ttsVoicesRef.current;

    // cancel any previous speech and clear abort
    stopSpeaking();
    ttsAbortRef.current = false;

    nudgeResume();

    const chunks = chunkForTTS(clean);
    const { voice, tag } = pickVoiceForLang(voices, langCode);

    let i = 0;
    setTtsBusy(true);
    const speakNext = () => {
      if (ttsAbortRef.current) { setTtsBusy(false); currentUtteranceRef.current = null; return; }
      if (i >= chunks.length) { setTtsBusy(false); currentUtteranceRef.current = null; return; }

      const u = new SpeechSynthesisUtterance(chunks[i]);
      currentUtteranceRef.current = u;

      u.lang = tag || (voice && voice.lang) || 'en-IN';
      if (voice) u.voice = voice;
      u.rate = 1.0; u.pitch = 1.0; u.volume = 1.0;

      u.onstart = nudgeResume;
      u.onend = () => { if (!ttsAbortRef.current) { i += 1; speakNext(); } else { setTtsBusy(false); } };
      u.onerror = () => { if (!ttsAbortRef.current) { i += 1; speakNext(); } else { setTtsBusy(false); } };

      try { window.speechSynthesis.speak(u); } catch { i += 1; speakNext(); }
    };
    speakNext();
  };

  /* ---------- Turndown ---------- */
  useEffect(() => {
    turndownRef.current = new TurndownService({ headingStyle: 'atx' });
  }, []);

  /* ---------- Helpers ---------- */
  const decodeHtmlEntities = (str) => {
    if (!str) return '';
    const t = document.createElement('textarea'); t.innerHTML = str;
    return t.value;
  };

  const sanitizeHtmlForConversion = (html) => {
    if (!html) return '';
    return html
      .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, '')
      .replace(/<style[\s\S]*?>[\s\S]*?<\/style>/gi, '')
      .replace(/<!--[\s\S]*?-->/g, '');
  };

  const normalizeContent = (content) => {
    if (!content) return '';
    const w = decodeHtmlEntities(String(content));
    return sanitizeHtmlForConversion(w).trim();
  };

  const detectLanguageFromText = (text) => {
    if (!text || !text.trim()) return 'und';
    const s = String(text).trim();
    const ranges = [
      { code: 'hi', test: /[\u0900-\u097F]/ }, { code: 'bn', test: /[\u0980-\u09FF]/ },
      { code: 'gu', test: /[\u0A80-\u0AFF]/ }, { code: 'pa', test: /[\u0A00-\u0A7F]/ },
      { code: 'ta', test: /[\u0B80-\u0BFF]/ }, { code: 'te', test: /[\u0C00-\u0C7F]/ },
      { code: 'kn', test: /[\u0C80-\u0CFF]/ }, { code: 'ml', test: /[\u0D00-\u0D7F]/ },
      { code: 'ur', test: /[\u0600-\u06FF]/ }, { code: 'en', test: /[A-Za-z]/ }
    ];
    for (const r of ranges) if (r.test.test(s)) return r.code;
    return 'und';
  };

  /* ---------- Speech Recognition ---------- */
  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { recognition.current = null; return; }
    recognition.current = new SR();
    recognition.current.continuous = false;
    recognition.current.interimResults = false;

    recognition.current.onresult = (event) => {
      const transcript = Array.from(event.results).map(r => r[0].transcript).join('');
      finalTranscript.current = transcript;
      setInput(transcript);
      setDetectedLang(detectLanguageFromText(transcript));
      setInputCameFromVoice(true);
    };
    recognition.current.onend = () => setIsRecording(false);
    recognition.current.onerror = (e) => {
      console.error('Speech recognition error', e);
      notyf.error('Voice input error');
      setIsRecording(false);
    };
  }, []);

  /* ---------- Load chat history (KEEP) ---------- */
  useEffect(() => {
    const initializeChat = async () => {
      if (!chatId) return;
      try {
        const res = await axios.get(`${API_BASE_URL}${ENDPOINTS.CHATS.GET_ALL}/${chatId}`, {
          headers: { Authorization: `Bearer ${cookies.token}` }
        });
        setMessages(res?.data?.[0]?.messages || []);
      } catch (err) {
        console.error('Failed to load chat', err);
        notyf.error('Failed to load chat');
      }
    };
    initializeChat();
  }, [chatId, cookies.token]);

  /* ---------- Scroll on new messages ---------- */
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  /* ---------- UI helpers (KEEP) ---------- */
  const handleCopy = (codeContent, uniqueId) => {
    setCopiedCodeId(uniqueId);
    navigator.clipboard.writeText(codeContent)
      .then(() => notyf.success('Copied to clipboard'))
      .catch(() => notyf.error('Copy failed'));
    if (copyTimeouts.current[uniqueId]) clearTimeout(copyTimeouts.current[uniqueId]);
    copyTimeouts.current[uniqueId] = setTimeout(() => {
      setCopiedCodeId(null);
      delete copyTimeouts.current[uniqueId];
    }, 2000);
  };

  const copyMessageText = async (msg, asMarkdown = false) => {
    try {
      let payload = msg.content || '';
      if (asMarkdown && turndownRef.current) {
        payload = turndownRef.current.turndown(normalizeContent(payload));
      }
      await navigator.clipboard.writeText(payload);
      notyf.success('Message copied');
    } catch (e) {
      console.error(e);
      notyf.error('Copy failed');
    }
  };

  const downloadMessage = (msg, asMarkdown = true) => {
    try {
      const raw = normalizeContent(msg.content || '');
      const data = asMarkdown && turndownRef.current ? turndownRef.current.turndown(raw) : raw;
      const blob = new Blob([data], { type: 'text/plain;charset=utf-8' });
      const fname = `${msg.sender || 'msg'}-${new Date(msg.created_at || Date.now()).toISOString().replace(/[:.]/g, '-')}.${asMarkdown ? 'md' : 'txt'}`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = fname;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      notyf.success('Download started');
    } catch (e) {
      console.error(e);
      notyf.error('Download failed');
    }
  };

  const showMessageDetails = (msg, index) => {
    const rawHtml = normalizeContent(msg.content || '');
    const md = turndownRef.current ? turndownRef.current.turndown(rawHtml) : '';
    const plain = (rawHtml.replace(/<\/?[^>]+(>|$)/g, '') || '').trim();
    const words = plain ? plain.split(/\s+/).length : 0;
    const chars = plain.length;
    const lang = detectLanguageFromText(plain || msg.content || '');
    const summary = plain.split(/\s+/).slice(0, 40).join(' ') + (words > 40 ? '…' : '');
    setDetailMsg({
      index,
      rawHtml,
      md,
      plain,
      words,
      chars,
      lang,
      summary,
      created_at: msg.created_at || new Date().toISOString(),
      sender: msg.sender
    });
    setDetailsOpen(true);
  };

  /* ---------- Input & SR controls ---------- */
  const getSpeechTagForSelected = () => {
    const found = LANGS.find(l => l.code === selectedLang);
    if (!found || !found.tag) {
      const nav = (navigator.language || navigator.userLanguage || '').toLowerCase();
      if (nav.startsWith('hi')) return 'hi-IN';
      if (nav.startsWith('mr')) return 'mr-IN';
      if (nav.startsWith('en')) return 'en-IN';
      return 'hi-IN';
    }
    return found.tag;
  };

  const toggleVoiceRecording = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR || !recognition.current) { notyf.error('Voice input not supported in this browser'); return; }
    if (isRecording) {
      try { recognition.current.stop(); } catch { }
      setIsRecording(false);
      return;
    }
    stopSpeaking(); // avoid feedback
    try { recognition.current.lang = getSpeechTagForSelected(); } catch { }
    finalTranscript.current = ''; setInput(''); setInputCameFromVoice(false);
    setIsRecording(true);
    try { recognition.current.start(); } catch (e) { console.error(e); setIsRecording(false); notyf.error('Unable to start voice'); }
  };

  const onInputChange = (e) => {
    const v = e.target.value;
    setInput(v);
    setDetectedLang(v.trim() ? detectLanguageFromText(v) : 'und');
    setInputCameFromVoice(false); // typing → no auto TTS
  };

  /* ---------- Send message (KEEP + TTS) ---------- */
  const sendMessage = async () => {
    if (!input || !input.trim()) return;
    const txt = input.trim();
    const userMsg = { content: txt, sender: 'user' };
    const updated = [...messages, userMsg];
    setMessages(updated);
    setInput('');
    setIsThinking(true);

    try {
      if (chatId) {
        await axios.patch(`${API_BASE_URL}${ENDPOINTS.CHATS.UPDATE}/${chatId}`, { messages: updated, title: txt }, { headers: { Authorization: `Bearer ${cookies.token}` } });
        window.dispatchEvent(new Event("chatsUpdated"));
      }
    } catch { /* non-blocking */ }

    const payload = { prompt: txt, chat_id: chatId };
    if (selectedLang !== 'und') payload.lang = selectedLang;
    else if (detectedLang !== 'und') payload.lang = detectedLang;

    try {
      const res = await axios.post(DEFAULT_API_URL, payload, { headers: { Authorization: `Bearer ${cookies.token}` } });
      const botText = res?.data?.data ?? '';
      const botMsg = { content: String(botText), sender: 'bot' };
      const newMsgs = [...updated, botMsg];
      setMessages(newMsgs);

      // Auto-speak ONLY if query came via voice
      if (inputCameFromVoice) {
        const langForTTS =
          selectedLang !== 'und' ? selectedLang :
            (detectedLang !== 'und' ? detectedLang : 'en');

        // Wait for DOM to render, prefer visible text; fallback to cleaner
        let attempts = 0;
        const maxAttempts = 12;
        const trySpeak = () => {
          const idx = newMsgs.length - 1;
          const node = msgRefs.current[idx];
          const visible = node ? (node.innerText || '').replace(/\s+/g, ' ').trim() : '';
          if (visible) {
            speakText(visible, langForTTS);
            return;
          }
          if (attempts++ < maxAttempts) requestAnimationFrame(trySpeak);
          else {
            const fallback = prepareTTSContent(String(botText));
            if (fallback) speakText(fallback, langForTTS);
          }
        };
        requestAnimationFrame(trySpeak);
      }

      try {
        if (chatId) {
          await axios.patch(`${API_BASE_URL}${ENDPOINTS.CHATS.UPDATE}/${chatId}`, { messages: newMsgs }, { headers: { Authorization: `Bearer ${cookies.token}` } });
        }
      } catch { /* silent */ }
    } catch (err) {
      console.error('AI request failed', err);
      setMessages(prev => [...prev, { content: 'Error: Could not get response', sender: 'bot' }]);
      notyf.error('AI request failed');
    } finally {
      setIsThinking(false);
      setDetectedLang('und');
      setInputCameFromVoice(false); // reset after one answer
    }
  };

  /* ---------- Details Modal (KEEP) ---------- */
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailMsg, setDetailMsg] = useState(null);

  const DetailsModal = ({ open, onClose, data }) => {
    if (!open || !data) return null;
    return (
      <div className="chat-details-modal-overlay" onClick={onClose}>
        <div className="chat-details-modal" onClick={(e) => e.stopPropagation()}>
          <header className="modal-header">
            <h3>Message details</h3>
            <small>{data.sender} • {new Date(data.created_at).toLocaleString()}</small>
          </header>
          <section className="modal-body">
            <div className="meta-row">
              <div><strong>Language</strong><div>{data.lang}</div></div>
              <div><strong>Words</strong><div>{data.words}</div></div>
              <div><strong>Chars</strong><div>{data.chars}</div></div>
            </div>
            <div style={{ marginTop: 12 }}>
              <strong>Summary</strong>
              <p style={{ marginTop: 6 }}>{data.summary}</p>
            </div>
            <div style={{ marginTop: 12 }}>
              <strong>Markdown</strong>
              <textarea readOnly value={data.md} style={{ width: '100%', height: 140, marginTop: 6, fontFamily: 'monospace', fontSize: 13 }} />
            </div>
            <div style={{ marginTop: 12 }}>
              <strong>Raw HTML</strong>
              <textarea readOnly value={data.rawHtml} style={{ width: '100%', height: 140, marginTop: 6, fontFamily: 'monospace', fontSize: 13 }} />
            </div>
          </section>
          <footer className="modal-footer">
            <button className="btn btn-sm btn-outline-secondary" onClick={onClose}>Close</button>
          </footer>
        </div>
      </div>
    );
  };

  /* ---------- Skeleton ---------- */
  const ThinkingSkeleton = () => (
    <div className="bot-message">
      <div className="message-content" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div className="skeleton-line" style={{ width: '70%' }} />
        <div className="skeleton-line" style={{ width: '95%' }} />
        <div className="skeleton-line" style={{ width: '60%' }} />
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <div className="typing-dot" />
          <div className="typing-dot" style={{ animationDelay: '0.12s' }} />
          <div className="typing-dot" style={{ animationDelay: '0.24s' }} />
        </div>
      </div>
    </div>
  );

  const langOptions = LANGS.map(l => <option key={l.code} value={l.code}>{l.label}</option>);

  /* =========================== RENDER =========================== */
  return (
    <main className="main-content">
      <MainHeader />
      <div className="container-fluid py-2 px-2">
        <div className="chat-part12">
          {messages.map((msg, i) =>
            msg.sender !== '' && (
              <div key={i + '-' + (msg.content?.slice(0, 12) || i)} className={`message ${msg.sender}-message`}>
                <div className="message-content markdown-content">
                  {msg.sender === 'bot' ? (
                    <div>
                      <div
                        ref={(el) => { if (el) msgRefs.current[i] = el; }}
                        onDoubleClick={() => {
                          const node = msgRefs.current[i];
                          const visible = node ? (node.innerText || '').replace(/\s+/g, ' ').trim() : '';
                          const raw = String(msg.content || '');
                          const toSpeak = visible || prepareTTSContent(raw);
                          if (!toSpeak) { notyf.error('Nothing to speak in this message'); return; }
                          const langForTTS =
                            selectedLang !== 'und' ? selectedLang :
                              (detectedLang !== 'und' ? detectedLang : 'en');
                          speakText(toSpeak, langForTTS);
                        }}
                        style={{ cursor: 'text' }}
                        title="Double-click to speak"
                      >
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm, remarkMath]}
                          rehypePlugins={[rehypeKatex, rehypeRaw]}
                          components={{
                            code({ inline, className, children, ...props }) {
                              const match = /language-(\w+)/.exec(className || '');
                              const codeContent = String(children).replace(/\n$/, '');
                              const uniqueId = crypto?.randomUUID?.() ?? `${i}-code`;
                              return !inline && match ? (
                                <CodeBlock
                                  language={match[1]}
                                  codeContent={codeContent}
                                  onCopy={() => handleCopy(codeContent, uniqueId)}
                                  copiedId={copiedCodeId}
                                  blockId={uniqueId}
                                />
                              ) : (
                                <code className="inline-code" {...props}>{children}</code>
                              );
                            },
                            a: ({ node, ...props }) => <a className="markdown-link" target="_blank" rel="noreferrer noopener" {...props} />,
                            img: ({ node, ...props }) => <img className="markdown-image" alt={props.alt || ''} {...props} />,
                            table: ({ node, ...props }) => <table className="markdown-table" {...props} />,
                            th: ({ node, ...props }) => <th className="markdown-th" {...props} />,
                            td: ({ node, ...props }) => <td className="markdown-td" {...props} />,
                          }}
                        >
                          {normalizeContent(msg.content)}
                        </ReactMarkdown>
                      </div>

                      {/* Actions (KEEP, plus Speak/Stop) */}
                      <div className="message-actions">
                        <button
                          title="Speak"
                          className="action-btn"
                          onClick={() => {
                            const node = msgRefs.current[i];
                            const visible = node ? (node.innerText || '').replace(/\s+/g, ' ').trim() : '';
                            const raw = String(msg.content || '');
                            const toSpeak = visible || prepareTTSContent(raw);
                            if (!toSpeak) { notyf.error('Nothing to speak in this message'); return; }
                            const langForTTS =
                              selectedLang !== 'und' ? selectedLang :
                                (detectedLang !== 'und' ? detectedLang : 'en');
                            speakText(toSpeak, langForTTS);
                          }}
                        >
                          <i className="fa-solid fa-volume-high"></i>
                        </button>

                        <button title="Stop" className="action-btn" onClick={stopSpeaking} disabled={!ttsBusy}>
                          <i className="fa-solid fa-stop"></i>
                        </button>

                        <button title="Copy" className="action-btn" onClick={() => copyMessageText(msg, false)}>
                          <i className="fa-regular fa-copy"></i>
                        </button>

                        <button title="Copy Markdown" className="action-btn" onClick={() => copyMessageText(msg, true)}>
                          <i className="fa-regular fa-file-lines"></i>
                        </button>

                        <button title="Download .md" className="action-btn" onClick={() => downloadMessage(msg, true)}>
                          <i className="fa-solid fa-download"></i>
                        </button>

                        <button title="Details" className="action-btn" onClick={() => showMessageDetails(msg, i)}>
                          <i className="fa-solid fa-circle-info"></i>
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div style={{ whiteSpace: 'pre-wrap' }}>{msg.content}</div>
                  )}
                </div>
              </div>
            )
          )}

          {isThinking && <ThinkingSkeleton />}

          <div ref={messagesEndRef} />
        </div>

        <div className="bt-search1" style={{ marginTop: 12 }}>
          {messages.length <= 1 && <div className="tld-finder-header">Hola 👋 What’s on your mind today?</div>}

          <div className="tld-finder-generator mb-4 d-flex align-items-center justify-content-between gap-2" style={{ gap: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
              <input
                type="text"
                className="form-control form-control-lg w-auto flex-grow-1"
                value={input}
                onChange={onInputChange}
                placeholder="अपना संदेश टाइप करें या बोलें...."
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                aria-label="Message input"
                style={{ minWidth: 200 }}
              />

              <select
                value={selectedLang}
                onChange={(e) => setSelectedLang(e.target.value)}
                title="Select language"
                className="form-control form-control-sm my-sel"
              >
                {langOptions}
              </select>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <button
                id="voice-btn"
                onClick={toggleVoiceRecording}
                className={`btn btn-lg ${isRecording ? 'recording' : ''}`}
                disabled={!('SpeechRecognition' in window || 'webkitSpeechRecognition' in window)}
                title={isRecording ? 'Stop recording' : 'Start voice input'}
              >
                <i className={`fas fa-${isRecording ? 'microphone-slash' : 'microphone'}`} />
              </button>

              <button className="btn btn-lg" onClick={sendMessage} title="Send message">
                <i className="fa-regular fa-circle-up" />
              </button>

              <button className="btn btn-sm btn-outline-danger" onClick={stopSpeaking} disabled={!ttsBusy} title="Stop speaking">
                ⏹
              </button>
            </div>
          </div>
        </div>
      </div>

      <DetailsModal open={detailsOpen} onClose={() => setDetailsOpen(false)} data={detailMsg} />
    </main>
  );
};

export default Chat;
